import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Collapse,
    Pagination,
    Paper,
    Stack,
    Typography,
    Button,
    Select,
    MenuItem,
    TextField,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    IconButton,
    CircularProgress,
    Tooltip,
    Link as MuiLink,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useIsMobile } from '../hooks/useBreakpoints';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { fetchPaymentRequests } from '../store/slices/paymentRequestSlice';
import type {
    ListFilter,
    PaymentRequestStatus,
    PaymentType,
} from '../types/paymentRequest';
import PaymentRequestStatusChip from './components/PaymentRequestStatusChip';

type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

const STATUSES: PaymentRequestStatus[] = [
    'Draft',
    'Submitted',
    'APPrepared',
    'CAReviewed',
    'FMApproved',
    'GMApproved',
    'FinalApproved',
    'Executed',
    'Returned',
    'Rejected',
    'Voided',
];

const PAYMENT_TYPES: PaymentType[] = ['Advance', 'AgainstGRPO'];

type QuickFilter = 'all' | 'pendingMyAction' | 'myDrafts';

const formatDate = (value: unknown): string => {
    if (!value) return '';
    const d = new Date(String(value));
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${month}-${yy}`;
};

const formatAmount = (amount: number, currency: string): string => {
    const value = Number(amount || 0);
    const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
    return currency ? `${formatted} ${currency}` : formatted;
};

const getRowAllocation = (row: any): string => {
    const code = row.projectCode || row.ProjectCode || row.projectcode;
    if (code) return String(code);
    const cc = row.costCenterCode || row.CostCenterCode || row.costcentercode;
    return cc ? String(cc) : '';
};

const getRowVendor = (row: any): string =>
    String(row.vendorCardCode || row.VendorCardCode || row.vendorcardcode || '');

const getRowDocRef = (row: any): string =>
    String(row.docRef || row.DocRef || row.docref || '');

const getRowAmount = (row: any): number =>
    Number(row.amountFigures ?? row.AmountFigures ?? row.amountfigures ?? 0);

const getRowCurrency = (row: any): string =>
    String(row.amountCurrency || row.AmountCurrency || row.amountcurrency || '');

const getRowType = (row: any): string =>
    String(row.paymentType || row.PaymentType || row.paymenttype || '');

const getRowStatus = (row: any): PaymentRequestStatus =>
    (row.status || row.Status) as PaymentRequestStatus;

const getRowCreatedDate = (row: any): string =>
    String(row.createdDate || row.CreatedDate || row.createddate || '');

const getRowUpdatedDate = (row: any): string =>
    String(row.updatedDate || row.UpdatedDate || row.updateddate || '');

const getRowId = (row: any): number =>
    Number(row.paymentRequestId || row.PaymentRequestId || row.paymentrequestid || 0);

const PaymentRequestList: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMobile = useIsMobile();
    const [filtersOpen, setFiltersOpen] = useState(false);

    const items = useSelector((s: RootState) => (s as any).paymentRequest?.items ?? []);
    const total = useSelector((s: RootState) => (s as any).paymentRequest?.total ?? 0);
    const isLoading = useSelector((s: RootState) => (s as any).paymentRequest?.isLoading ?? false);
    const error = useSelector((s: RootState) => (s as any).paymentRequest?.error ?? null);

    // Filter state derived from URL params
    const status = searchParams.get('status') || '';
    const paymentType = searchParams.get('paymentType') || '';
    const vendor = searchParams.get('vendor') || '';
    const allocation = searchParams.get('allocation') || '';
    const dateFrom = searchParams.get('dateFrom') || '';
    const dateTo = searchParams.get('dateTo') || '';
    const q = searchParams.get('q') || '';
    const pendingMyAction = searchParams.get('pendingMyAction') === 'true';
    const page = Number(searchParams.get('page') || '0');
    const pageSize = Number(searchParams.get('pageSize') || '25');

    // Local input state to avoid spamming URL on each keystroke
    const [vendorInput, setVendorInput] = useState(vendor);
    const [allocationInput, setAllocationInput] = useState(allocation);
    const [qInput, setQInput] = useState(q);

    // Sync local inputs when URL changes externally (e.g., quick filters)
    useEffect(() => {
        setVendorInput(vendor);
    }, [vendor]);
    useEffect(() => {
        setAllocationInput(allocation);
    }, [allocation]);
    useEffect(() => {
        setQInput(q);
    }, [q]);

    const updateParams = useCallback(
        (updates: Record<string, string | undefined | null | boolean>) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                Object.entries(updates).forEach(([k, v]) => {
                    if (v === undefined || v === null || v === '' || v === false) {
                        next.delete(k);
                    } else {
                        next.set(k, String(v));
                    }
                });
                return next;
            });
        },
        [setSearchParams],
    );

    const activeQuickFilter: QuickFilter = useMemo(() => {
        if (pendingMyAction) return 'pendingMyAction';
        if (status === 'Draft') return 'myDrafts';
        return 'all';
    }, [pendingMyAction, status]);

    const setQuickFilter = (qf: QuickFilter) => {
        if (qf === 'pendingMyAction') {
            setSearchParams((prev) => {
                const next = new URLSearchParams();
                next.set('pendingMyAction', 'true');
                const ps = prev.get('pageSize');
                if (ps) next.set('pageSize', ps);
                return next;
            });
        } else if (qf === 'myDrafts') {
            setSearchParams((prev) => {
                const next = new URLSearchParams();
                next.set('status', 'Draft');
                const ps = prev.get('pageSize');
                if (ps) next.set('pageSize', ps);
                return next;
            });
        } else {
            setSearchParams((prev) => {
                const next = new URLSearchParams();
                const ps = prev.get('pageSize');
                if (ps) next.set('pageSize', ps);
                return next;
            });
        }
    };

    // Build the filter from the current URL/query state. Extracted as a
    // standalone callback so the toolbar Refresh button can re-trigger
    // a fetch without forcing any filter / pagination change.
    const reloadList = useCallback(() => {
        const filter: ListFilter = {
            page: page + 1, // backend is 1-based
            limit: pageSize,
        };
        if (status) filter.status = status as PaymentRequestStatus;
        if (paymentType) filter.paymentType = paymentType as PaymentType;
        if (vendor) filter.vendorCardCode = vendor;
        if (allocation) filter.projectCode = allocation;
        if (dateFrom) filter.dateFrom = dateFrom;
        if (dateTo) filter.dateTo = dateTo;
        if (q) filter.q = q;
        if (pendingMyAction) filter.pendingMyAction = true;
        dispatch(fetchPaymentRequests(filter));
    }, [
        dispatch,
        status,
        paymentType,
        vendor,
        allocation,
        dateFrom,
        dateTo,
        q,
        pendingMyAction,
        page,
        pageSize,
    ]);

    // Auto-fetch on filter / pagination change.
    useEffect(() => {
        reloadList();
    }, [reloadList]);

    const handleSearchSubmit = () => {
        updateParams({
            vendor: vendorInput.trim() || undefined,
            allocation: allocationInput.trim() || undefined,
            q: qInput.trim() || undefined,
            page: '0',
        });
    };

    const handleClearFilters = () => {
        setVendorInput('');
        setAllocationInput('');
        setQInput('');
        setSearchParams(new URLSearchParams());
    };

    const handleNew = () => navigate('/payment-requests/new');

    const rows = useMemo(
        () =>
            (items as any[]).map((row) => {
                const id = getRowId(row);
                return {
                    id,
                    docRef: getRowDocRef(row),
                    createdDate: getRowCreatedDate(row),
                    vendor: getRowVendor(row),
                    allocation: getRowAllocation(row),
                    paymentType: getRowType(row),
                    amount: getRowAmount(row),
                    currency: getRowCurrency(row),
                    status: getRowStatus(row),
                    updatedDate: getRowUpdatedDate(row),
                };
            }),
        [items],
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'docRef',
                headerName: 'DocRef',
                // DocRefs are e.g. "ALDHOW/GEN/1IDS030/PR/1/26" — ~26 chars.
                // flex makes the column take remaining space; minWidth keeps the
                // full ref visible at common viewport sizes.
                flex: 1.4,
                minWidth: 280,
                renderCell: (p) => (
                    <Tooltip title={String(p.value || '')} placement="top-start" arrow>
                        <MuiLink
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => navigate(`/payment-requests/${p.row.id}`)}
                            sx={{
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                fontSize: 13,
                                fontWeight: 600,
                                color: 'primary.main',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                            }}
                        >
                            {p.value}
                        </MuiLink>
                    </Tooltip>
                ),
            },
            {
                field: 'createdDate',
                headerName: 'Created',
                width: 110,
                valueFormatter: (p) => formatDate(p.value),
            },
            { field: 'vendor', headerName: 'Vendor', width: 140 },
            { field: 'allocation', headerName: 'Allocation', width: 140 },
            { field: 'paymentType', headerName: 'Type', width: 130 },
            {
                field: 'amount',
                headerName: 'Amount',
                width: 160,
                align: 'right',
                headerAlign: 'right',
                renderCell: (p) => formatAmount(Number(p.value), p.row.currency),
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 140,
                renderCell: (p) =>
                    p.value ? (
                        <PaymentRequestStatusChip status={p.value as PaymentRequestStatus} />
                    ) : null,
            },
            {
                field: 'updatedDate',
                headerName: 'Updated',
                width: 110,
                valueFormatter: (p) => formatDate(p.value),
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 90,
                sortable: false,
                filterable: false,
                renderCell: (p) => (
                    <IconButton
                        size="small"
                        aria-label="View payment request"
                        onClick={() => navigate(`/payment-requests/${p.row.id}`)}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                ),
            },
        ],
        [navigate],
    );

    const isEmpty = !isLoading && rows.length === 0;

    const handlePaginationChange = (model: GridPaginationModel) => {
        updateParams({
            page: String(model.page),
            pageSize: String(model.pageSize),
        });
    };

    return (
        <Paper
            component="section"
            aria-labelledby="prl-heading"
            elevation={0}
            sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                minHeight: 'calc(100vh - 100px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            {/* Top bar */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={{ xs: 1.5, sm: 0 }}
            >
                <Typography
                    component="h1"
                    id="prl-heading"
                    variant="h5"
                    sx={{ fontWeight: 700, fontSize: { xs: 20, sm: 22, md: 24 } }}
                >
                    Payment Requests
                </Typography>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    <Tooltip title="Reload the list with the current filters">
                        <span style={{ flex: 1 }}>
                            <Button
                                variant="outlined"
                                fullWidth={isMobile}
                                startIcon={
                                    isLoading ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <RefreshIcon />
                                    )
                                }
                                onClick={reloadList}
                                disabled={isLoading}
                            >
                                Refresh
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="contained"
                        fullWidth={isMobile}
                        startIcon={<AddIcon />}
                        onClick={handleNew}
                        sx={{ flex: 1 }}
                    >
                        {isMobile ? 'New' : 'New Payment Request'}
                    </Button>
                </Stack>
            </Stack>

            {/* Quick filter chips — horizontal scroll on overflow on mobile */}
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                role="group"
                aria-label="Quick filters"
                sx={{
                    overflowX: 'auto',
                    pb: { xs: 0.5, md: 0 },
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                <Chip
                    label="Pending my action"
                    color={activeQuickFilter === 'pendingMyAction' ? 'primary' : 'default'}
                    variant={activeQuickFilter === 'pendingMyAction' ? 'filled' : 'outlined'}
                    onClick={() => setQuickFilter('pendingMyAction')}
                    clickable
                />
                <Chip
                    label="My drafts"
                    color={activeQuickFilter === 'myDrafts' ? 'primary' : 'default'}
                    variant={activeQuickFilter === 'myDrafts' ? 'filled' : 'outlined'}
                    onClick={() => setQuickFilter('myDrafts')}
                    clickable
                />
                <Chip
                    label="All"
                    color={activeQuickFilter === 'all' ? 'primary' : 'default'}
                    variant={activeQuickFilter === 'all' ? 'filled' : 'outlined'}
                    onClick={() => setQuickFilter('all')}
                    clickable
                />
            </Stack>

            {/* Filter bar — collapsible on mobile to reclaim vertical space */}
            {isMobile && (
                <Button
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFiltersOpen((v) => !v)}
                    aria-expanded={filtersOpen}
                    aria-controls="prl-filters"
                    fullWidth
                >
                    {filtersOpen ? 'Hide filters' : 'Show filters'}
                </Button>
            )}
            <Collapse in={!isMobile || filtersOpen} unmountOnExit={false}>
            <Box
                id="prl-filters"
                role="region"
                aria-label="Payment Request filters"
                sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(auto-fit, minmax(180px, 1fr))',
                    },
                    p: { xs: 1.5, md: 2 },
                    bgcolor: '#fafafa',
                    border: '1px solid #eee',
                    borderRadius: 1,
                }}
            >
                <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={status}
                        label="Status"
                        onChange={(e) =>
                            updateParams({ status: e.target.value || undefined, page: '0' })
                        }
                    >
                        <MenuItem value="">All</MenuItem>
                        {STATUSES.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={paymentType}
                        label="Type"
                        onChange={(e) =>
                            updateParams({ paymentType: e.target.value || undefined, page: '0' })
                        }
                    >
                        <MenuItem value="">All</MenuItem>
                        {PAYMENT_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    label="Vendor (CardCode)"
                    value={vendorInput}
                    onChange={(e) => setVendorInput(e.target.value)}
                    onBlur={handleSearchSubmit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSearchSubmit();
                    }}
                />

                <TextField
                    size="small"
                    label="Project / CC"
                    value={allocationInput}
                    onChange={(e) => setAllocationInput(e.target.value)}
                    onBlur={handleSearchSubmit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSearchSubmit();
                    }}
                />

                <TextField
                    size="small"
                    label="Date from"
                    type="date"
                    value={dateFrom}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) =>
                        updateParams({ dateFrom: e.target.value || undefined, page: '0' })
                    }
                />

                <TextField
                    size="small"
                    label="Date to"
                    type="date"
                    value={dateTo}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) =>
                        updateParams({ dateTo: e.target.value || undefined, page: '0' })
                    }
                />

                <TextField
                    size="small"
                    label="Search"
                    placeholder="DocRef, invoice…"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onBlur={handleSearchSubmit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSearchSubmit();
                    }}
                />

                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={handleSearchSubmit}>
                        Apply
                    </Button>
                    <Button size="small" onClick={handleClearFilters}>
                        Clear
                    </Button>
                </Stack>
            </Box>
            </Collapse>

            {/* Error */}
            {error && (
                <Alert severity="error" onClose={() => undefined}>
                    {String(error)}
                </Alert>
            )}

            {/* Body */}
            <Box sx={{ flexGrow: 1, minHeight: 400, position: 'relative' }}>
                {isLoading && rows.length === 0 ? (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ height: 400 }}
                        role="status"
                        aria-live="polite"
                    >
                        <CircularProgress aria-label="Loading payment requests" />
                    </Stack>
                ) : isEmpty ? (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        spacing={2}
                        sx={{ height: 400, color: 'text.secondary' }}
                        role="status"
                    >
                        <Typography>
                            No payment requests yet. Click + New Payment Request to create one.
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleNew}
                        >
                            New Payment Request
                        </Button>
                    </Stack>
                ) : isMobile ? (
                    <Stack
                        component="ul"
                        role="list"
                        aria-label="Payment Requests"
                        spacing={1}
                        sx={{ listStyle: 'none', p: 0, m: 0 }}
                    >
                        {rows.map((row) => (
                            <Card
                                key={row.id}
                                component="li"
                                variant="outlined"
                                sx={{ borderRadius: 1.5 }}
                            >
                                <CardActionArea
                                    onClick={() => navigate(`/payment-requests/${row.id}`)}
                                    aria-label={`Open payment request ${row.docRef}`}
                                >
                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="flex-start"
                                            spacing={1}
                                        >
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily:
                                                            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color: 'primary.main',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {row.docRef}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mt: 0.25 }}
                                                >
                                                    {row.vendor || '—'} · {row.allocation || '—'}
                                                </Typography>
                                            </Box>
                                            {row.status && (
                                                <PaymentRequestStatusChip status={row.status} />
                                            )}
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="baseline"
                                            sx={{ mt: 1 }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {row.paymentType || '—'}
                                                {row.createdDate ? ` · ${formatDate(row.createdDate)}` : ''}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}
                                            >
                                                {formatAmount(row.amount, row.currency)}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                        {/* Mobile pagination */}
                        {total > pageSize && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                <Pagination
                                    count={Math.max(1, Math.ceil(total / pageSize))}
                                    page={page + 1}
                                    onChange={(_, p) =>
                                        updateParams({ page: String(p - 1) })
                                    }
                                    size="small"
                                    siblingCount={0}
                                    boundaryCount={1}
                                />
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        paginationMode="server"
                        rowCount={total}
                        paginationModel={{ page, pageSize }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        onPaginationModelChange={handlePaginationChange}
                        disableRowSelectionOnClick
                        autoHeight
                        aria-label="Payment Requests"
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f5f5f5',
                                fontWeight: 600,
                            },
                        }}
                    />
                )}
            </Box>
        </Paper>
    );
};

export default PaymentRequestList;
